"use client";

// Per-command panel components. Each is a small wrapper around
// PromptPanel + a fetch call. They live together so command
// authors can audit the full set in one file when adding new
// mutation commands.
import { PromptPanel } from "@/components/command-bar/panels/prompt-panel";
import { commandPost, toastOk, toastError } from "@/lib/commands/api";
import type { CommandContext } from "@/lib/commands/types";

interface PanelProps {
  ctx: CommandContext;
  onClose: () => void;
}

export function ExpandRadiusPanel({ ctx, onClose }: PanelProps) {
  if (!ctx.fulfillmentRequestId) {
    onClose();
    return null;
  }
  return (
    <PromptPanel
      options={{
        title: "Expand fulfillment radius",
        description: "Add miles to the current search radius.",
        label: "Additional miles",
        type: "number",
        placeholder: "10",
        chips: [
          { label: "+5 mi", value: "5" },
          { label: "+10 mi", value: "10" },
          { label: "+20 mi", value: "20" },
        ],
      }}
      onClose={onClose}
      onSubmit={async (value) => {
        const miles = parseInt(value, 10);
        if (!Number.isFinite(miles) || miles <= 0) {
          toastError("Enter a positive number of miles");
          throw new Error("invalid miles");
        }
        try {
          await commandPost(
            `/api/v1/fulfillment/${ctx.fulfillmentRequestId}/expand-radius`,
            { radius_miles: miles },
          );
          toastOk("Radius expanded");
        } catch (e) {
          toastError(e instanceof Error ? e.message : "Failed to expand radius");
          throw e;
        }
      }}
    />
  );
}

export function BlastDriverPanel({ ctx, onClose }: PanelProps) {
  if (!ctx.fulfillmentRequestId) {
    onClose();
    return null;
  }
  return (
    <PromptPanel
      options={{
        title: "Blast a specific driver",
        description: "Send an immediate offer to one of your drivers.",
        label: "Driver ID",
        placeholder: "drv_...",
        submitLabel: "Send offer",
      }}
      onClose={onClose}
      onSubmit={async (driverId) => {
        try {
          await commandPost(
            `/api/v1/fulfillment/${ctx.fulfillmentRequestId}/blast-driver`,
            { driver_id: driverId.trim() },
          );
          toastOk("Offer sent");
        } catch (e) {
          toastError(e instanceof Error ? e.message : "Failed to send offer");
          throw e;
        }
      }}
    />
  );
}

export function BlastCarrierPanel({ ctx, onClose }: PanelProps) {
  if (!ctx.fulfillmentRequestId) {
    onClose();
    return null;
  }
  return (
    <PromptPanel
      options={{
        title: "Blast a specific carrier",
        description: "Send an offer to a marketplace carrier directly.",
        label: "Carrier ID",
        placeholder: "car_...",
        submitLabel: "Send offer",
      }}
      onClose={onClose}
      onSubmit={async (carrierId) => {
        try {
          await commandPost(
            `/api/v1/fulfillment/${ctx.fulfillmentRequestId}/blast-carrier`,
            { carrier_id: carrierId.trim() },
          );
          toastOk("Offer sent");
        } catch (e) {
          toastError(e instanceof Error ? e.message : "Failed to send offer");
          throw e;
        }
      }}
    />
  );
}

export function RestartFulfillmentPanel({ ctx, onClose }: PanelProps) {
  if (!ctx.loadId) {
    onClose();
    return null;
  }
  return (
    <PromptPanel
      options={{
        title: "Restart fulfillment",
        description:
          "Start a fresh fulfillment cycle on this load. Optionally override the search radius.",
        label: "Radius (miles, optional)",
        type: "number",
        placeholder: "leave blank to use defaults",
        submitLabel: "Restart",
      }}
      onClose={onClose}
      onSubmit={async (value) => {
        const radius = value.trim() ? parseInt(value.trim(), 10) : null;
        const body: { override?: { search_radius_miles: number } } = {};
        if (radius && Number.isFinite(radius) && radius > 0) {
          body.override = { search_radius_miles: radius };
        }
        try {
          await commandPost(`/api/v1/loads/${ctx.loadId}/fulfillment/start`, body);
          toastOk("Fulfillment restarted");
        } catch (e) {
          toastError(
            e instanceof Error ? e.message : "Failed to restart fulfillment",
          );
          throw e;
        }
      }}
    />
  );
}

export function AdjustLedgerPanel({ ctx, onClose }: PanelProps) {
  if (!ctx.driverId) {
    onClose();
    return null;
  }
  return (
    <PromptPanel
      options={{
        title: "Adjust driver ledger",
        description: "Apply a manual adjustment. Reason is required.",
        label: "Reason and amount (e.g. '+25.00 wait-time bonus')",
        placeholder: "Describe the adjustment",
        type: "textarea",
        submitLabel: "Apply",
      }}
      onClose={onClose}
      onSubmit={async (reason) => {
        if (!reason.trim()) {
          toastError("Reason is required");
          throw new Error("missing reason");
        }
        try {
          await commandPost(`/api/v1/payouts/drivers/${ctx.driverId}/ledger/adjustment`, {
            reason: reason.trim(),
          });
          toastOk("Adjustment recorded");
        } catch (e) {
          toastError(e instanceof Error ? e.message : "Failed to apply adjustment");
          throw e;
        }
      }}
    />
  );
}

// Note: the previous RefundChargePanel was removed because it POSTed
// to /api/v1/billing/orders/:id/refund — a non-existent route — and
// the command bar had no chargeId in context to satisfy the real
// endpoint at /api/v1/billing/charges/:chargeId/refund. The
// per-charge refund flow now lives on the customer billing page
// (Charges tab) where each row can launch a properly-scoped
// RefundDialog. See launch-plan.md § 19.

export function IssueCreditPanel({ ctx, onClose }: PanelProps) {
  if (!ctx.customerId) {
    onClose();
    return null;
  }
  return (
    <PromptPanel
      options={{
        title: "Issue customer credit",
        description: "Apply a credit to this customer. Reason is required.",
        label: "Amount and reason",
        placeholder: "$50 — goodwill",
        type: "textarea",
        submitLabel: "Issue credit",
      }}
      onClose={onClose}
      onSubmit={async (reason) => {
        if (!reason.trim()) {
          toastError("Reason is required");
          throw new Error("missing reason");
        }
        try {
          await commandPost(`/api/v1/billing/customers/${ctx.customerId}/credits`, {
            reason: reason.trim(),
          });
          toastOk("Credit issued");
        } catch (e) {
          toastError(e instanceof Error ? e.message : "Failed to issue credit");
          throw e;
        }
      }}
    />
  );
}

export function RepriceOrderPanel({ ctx, onClose }: PanelProps) {
  if (!ctx.orderId) {
    onClose();
    return null;
  }
  return (
    <PromptPanel
      options={{
        title: "Reprice order",
        description: "Recompute the order's price using the current pricing engine.",
        label: "Reason",
        placeholder: "Manual reprice — corrected weight",
        type: "textarea",
        submitLabel: "Reprice",
      }}
      onClose={onClose}
      onSubmit={async (reason) => {
        if (!reason.trim()) {
          toastError("Reason is required");
          throw new Error("missing reason");
        }
        try {
          await commandPost(`/api/v1/pricing/order-prices/${ctx.orderId}/reprice`, {
            reason: reason.trim(),
          });
          toastOk("Order repriced");
        } catch (e) {
          toastError(e instanceof Error ? e.message : "Failed to reprice");
          throw e;
        }
      }}
    />
  );
}

export function BumpPayoutPanel({ ctx, onClose }: PanelProps) {
  // Bumps target the offer attached to the current load. The handler
  // walks the load → offer relationship server-side; we just need
  // the amount delta + reason.
  if (!ctx.loadId) {
    onClose();
    return null;
  }
  return (
    <PromptPanel
      options={{
        title: "Bump driver payout",
        description: "Raise the offer so drivers are more likely to accept.",
        label: "Additional dollars",
        type: "number",
        placeholder: "10",
        chips: [
          { label: "+$5", value: "5" },
          { label: "+$10", value: "10" },
          { label: "+$20", value: "20" },
        ],
        submitLabel: "Bump",
      }}
      onClose={onClose}
      onSubmit={async (value) => {
        const dollars = parseInt(value, 10);
        if (!Number.isFinite(dollars) || dollars <= 0) {
          toastError("Enter a positive dollar amount");
          throw new Error("invalid amount");
        }
        try {
          // Server resolves load → offer and applies the bump.
          await commandPost(`/api/v1/payouts/loads/${ctx.loadId}/bump`, {
            additional_cents: dollars * 100,
          });
          toastOk("Offer bumped");
        } catch (e) {
          toastError(e instanceof Error ? e.message : "Failed to bump offer");
          throw e;
        }
      }}
    />
  );
}
